// ============================================================================
// /A/:assetId — where a physical QR tag lands. PUBLIC route, no shell, no auth.
// (Also /a/, and /T/ + /t/ from PR 301 — see TAG_ROUTE_PATHS in asset-tag.ts.
// A path that has ever been printable is never retired.)
//
// ARCHITECTURE, AND WHY IT IS NOT A CLOUD FUNCTION.
// The design memo specified a Cloud Function resolver behind a hosting rewrite.
// Building it revealed that is strictly worse here. Firebase Hosting already
// rewrites ** to index.html, so /A/FM-0007 reaches the SPA with no function at
// all — and doing it in the SPA removes a cold start from the critical path of
// a feature whose whole promise is speed, removes a second deploy surface,
// removes CORS, and removes the possibility of the resolver leaking data
// because it never queries anything.
//
// The indirection that MATTERS is preserved, which was the actual requirement:
// /A/<id> is a stable printed path that can be re-pointed to any future route
// without reprinting a single label. That is GS1's rule — the printed URI
// identifies the entity, and where it sends you is a separate, mutable concern.
//
// THE AUTH HANDOFF, solved by not needing a handoff. A camera scan opens the
// default browser, which may not hold the app's session — and Firebase's own
// docs say signInWithRedirect is broken on Chrome 115+/Safari 16.1+/Firefox
// 109+. So this route never redirects to a login page and never needs a ?next=
// parameter: it renders the public page IN PLACE at the scanned URL, and when
// sign-in succeeds the component re-renders and forwards. The URL was never
// lost, so there is nothing to restore. (The app's own convention already
// works this way — NavShell renders sign-in in place rather than redirecting.)
//
// WHAT AN ANONYMOUS SCANNER SEES: the asset id, the company name, and how to
// reach us. Nothing else. No facility name, no address, no employee name, no
// make or model, no inventory. Critically, this page makes NO database call of
// any kind, so it cannot leak a row and cannot be used as an existence oracle:
// a real id, an unassigned id and a nonsense id all render the same bytes.
// ============================================================================
import { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../shared/auth';
import { parseAssetId } from './asset-tag';

/** Public contact. A role mailbox, never an individual, so it outlives whoever
 *  is sitting in the seat. Ben set the address on 2026-10-13; note it is on the
 *  SECONDARY domain, not the primary one in tcs_information, and that is
 *  deliberate rather than a typo. */
const CONTACT_EMAIL = 'accounts@terminalcleaning.com';
/** The NATIONAL ACCOUNTS MANAGER's line — that POSITION owns floor-machine
 *  acquisition and repair, so a stranger holding a scrubber reaches the right
 *  desk. ⛔ A position, never a person (Ben's standing rule): the number happens
 *  to be EID-0018's today, and if the seat changes hands this constant changes
 *  with it — it is not a claim about who Jack Stuber is. Hardcoded rather than
 *  queried on purpose: this page makes NO database call, which is what makes it
 *  impossible to leak from or to probe.
 *
 *  ✅ As of the rev 2 tag artwork (2026-10-13) NO contact detail is printed on
 *  the label at all — it carries the asset id and the ownership line and
 *  nothing else. The QR encodes only HTTPS://APP.TERMINALCLEANING.COM/T/<ID>.
 *  So this constant is now free to change on its own: edit it, merge, and every
 *  tag already stuck on a machine shows the new number on the next scan. No
 *  reprint, no re-tagging, no dead labels. Setting it to '' hides the Call row
 *  outright. */
const CONTACT_PHONE: string = '(567) 454-9340';

export default function TagResolver() {
  const { assetId: raw } = useParams<{ assetId: string }>();
  const { user, profile, loading, signIn } = useAuth();
  const navigate = useNavigate();

  const { id: assetId, wellFormed, route } = parseAssetId(raw);

  // Signed in and the id looks like ours: forward to the real screen. `replace`
  // so Back returns to whatever they were doing, not to this shim.
  const signedIn = !!user && !!profile;

  useEffect(() => {
    if (loading) return;
    if (signedIn && route) {
      navigate(`${route}?src=qr`, { replace: true });
    }
  }, [loading, signedIn, route, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-tcs-bg-muted p-6">
        <p className="text-sm text-tcs-text-muted">Checking your sign-in…</p>
      </div>
    );
  }

  if (signedIn && route) {
    return <Navigate to={`${route}?src=qr`} replace />;
  }

  // Everything else — not signed in, or an id shape we do not recognise — gets
  // the identical public page. Same markup, same status, no lookup.
  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-tcs-bg-muted p-6">
      <div className="w-full max-w-sm rounded-2xl border border-tcs-border bg-tcs-raised p-6 text-center shadow-sm">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-tcs-inverse text-lg font-bold tracking-wide text-tcs-inverse-ink">
          TCS
        </div>

        <h1 className="mt-4 text-xl font-bold leading-snug text-tcs-text">
          Property of
          <br />
          Terminal Cleaning Services
        </h1>

        <p className="mt-3 text-sm text-tcs-text-secondary">
          You have scanned company equipment. If this item is lost, damaged, or somewhere it should
          not be, please let us know.
        </p>

        <dl className="mt-5 divide-y divide-tcs-border border-y border-tcs-border text-sm">
          {wellFormed && (
            <div className="flex justify-between gap-3 py-2.5">
              <dt className="text-tcs-text-muted">Asset</dt>
              <dd className="font-mono font-medium text-tcs-text">{assetId}</dd>
            </div>
          )}
          {CONTACT_PHONE && (
            <div className="flex justify-between gap-3 py-2.5">
              <dt className="text-tcs-text-muted">Call</dt>
              <dd className="font-medium text-tcs-text">
                <a href={`tel:${CONTACT_PHONE.replace(/[^\d+]/g, '')}`}>{CONTACT_PHONE}</a>
              </dd>
            </div>
          )}
          <div className="flex justify-between gap-3 py-2.5">
            <dt className="shrink-0 text-tcs-text-muted">Email</dt>
            <dd className="min-w-0 break-all text-right font-medium text-tcs-text">
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </dd>
          </div>
        </dl>

        <button
          type="button"
          onClick={() => {
            void signIn();
          }}
          className="mt-5 flex min-h-[3rem] w-full items-center justify-center rounded-xl bg-tcs-inverse px-4 text-base font-semibold text-tcs-inverse-ink"
        >
          TCS employee? Sign in
        </button>
        <p className="mt-2 text-[11.5px] text-tcs-text-muted">
          Signing in brings you straight to this machine.
        </p>
      </div>
    </div>
  );
}
